// utils/HTMLReportGenerator/ReportStyles.js

export class ReportStyles {
  /**
   * Générer tous les styles CSS du rapport - Style Minimaliste
   */
  static generateStyles() {
    return `
        body { 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; 
            margin: 0; 
            padding: 40px; 
            line-height: 1.6; 
            background: #ffffff;
            color: #1a202c;
        }
        .container { max-width: 1200px; margin: 0 auto; }
        
        ${this.getHeaderStyles()}
        ${this.getSummaryStyles()}
        ${this.getTreeViewStyles()}
        ${this.getFilterStyles()}
        ${this.getStateChipStyles()}
        ${this.getMessageStyles()}
        ${this.getRule08Styles()}
        ${this.getFooterStyles()}
        ${this.getResponsiveStyles()}
    `;
  }

  /**
   * Styles de l'en-tête - Minimaliste avec Logo
   */
  static getHeaderStyles() {
    return `
        .header { 
            text-align: center; 
            margin-bottom: 60px; 
            border-bottom: 3px solid #4299e1;
            padding-bottom: 30px;
        }
        .header-content {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 24px;
        }
        .header-logo {
            height: 60px;
            width: auto;
            object-fit: contain;
        }
        .header-text {
            text-align: left;
        }
        .header h1 { 
            color: #2d3748; 
            margin: 0 0 10px 0; 
            font-size: 3em;
            font-weight: 300;
            letter-spacing: -1px;
        }
        .header p { 
            color: #718096; 
            margin: 0;
            font-size: 1.1em;
            font-weight: 400;
        }
        
        @media (max-width: 768px) {
            .header-content {
                flex-direction: column;
                gap: 16px;
            }
            .header-text {
                text-align: center;
            }
            .header-logo {
                height: 50px;
            }
        }
    `;
  }

  /**
   * Styles du résumé - Minimaliste
   */
  static getSummaryStyles() {
    return `
        .summary { 
            margin-bottom: 50px;
        }
        .summary h2 { 
            color: #2d3748; 
            margin: 0 0 30px 0;
            font-size: 1.8em;
            font-weight: 400;
            border-left: 4px solid #4299e1;
            padding-left: 20px;
        }
        .summary-grid { 
            display: grid; 
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
            gap: 0;
        }
        .summary-item { 
            padding: 30px; 
            text-align: center;
            border-right: 1px solid #e2e8f0;
            transition: all 0.3s ease;
        }
        .summary-item:last-child { border-right: none; }
        .summary-item:hover { 
            background: #f7fafc; 
            transform: translateY(-2px);
        }
        
        .summary-value { 
            font-size: 3em; 
            font-weight: 200; 
            margin: 0;
            color: #2d3748;
        }
        .summary-label { 
            color: #718096; 
            font-size: 0.9em;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-top: 5px;
            font-weight: 500;
        }
        .success { color: #38a169; }
        .warning { color: #ed8936; }
        .error { color: #e53e3e; }
    `;
  }

  /**
   * Styles du TreeView - Minimaliste
   */
  static getTreeViewStyles() {
    return `
        .treeview-container {
            border-top: 1px solid #e2e8f0;
            padding-top: 40px;
        }
        .treeview-container h2 {
            color: #2d3748;
            margin: 0 0 30px 0;
            font-size: 1.8em;
            font-weight: 400;
            border-left: 4px solid #4299e1;
            padding-left: 20px;
        }
        
        .tree {
            font-family: inherit;
        }
        .tree-node {
            margin: 0;
        }
        .tree-item {
            display: flex;
            align-items: center;
            padding: 12px 0;
            cursor: pointer;
            margin: 2px 0;
            transition: all 0.2s ease;
            min-height: 32px;
            border-bottom: 1px solid transparent;
        }
        .tree-item:hover {
            background: #f7fafc;
            border-bottom-color: #e2e8f0;
        }
        .tree-item.clickable:hover {
            background: #ebf8ff;
            border-bottom-color: #4299e1;
        }
        
        .tree-toggle {
            width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #718096;
            font-size: 12px;
            margin-right: 8px;
            flex-shrink: 0;
            font-weight: 600;
        }
        .tree-spacer {
            width: 18px;
            height: 18px;
            margin-right: 8px;
            flex-shrink: 0;
        }
        .tree-icon {
            margin-right: 8px;
            font-size: 14px;
            flex-shrink: 0;
        }
        .tree-label {
            font-weight: 500;
            color: #2d3748;
            margin-right: 12px;
            font-size: 14px;
        }
        .tree-meta {
            color: #718096;
            font-size: 12px;
            margin-right: auto;
            display: flex;
            gap: 8px;
            align-items: center;
        }
        .tree-stats {
            color: #718096;
            font-size: 12px;
            margin-left: auto;
            font-weight: 500;
        }
        
        .stat-ok { color: #38a169; font-weight: 600; }
        .stat-warning { color: #ed8936; font-weight: 600; }
        .stat-error { color: #e53e3e; font-weight: 600; }
        
        .tree-children {
            margin-left: 20px;
            padding-left: 0;
            border-left: 2px solid #f7fafc;
        }
        
        .tiroir-node .tree-label { 
            font-size: 16px; 
            font-weight: 600;
        }
        .tiroir-node.error .tree-label { color: #e53e3e; }
        .tiroir-node.warning .tree-label { color: #ed8936; }
        .tiroir-node.ok .tree-label { color: #38a169; }
        
        .module-node .tree-label { 
            font-size: 15px; 
            font-weight: 500;
        }
        .module-node.error .tree-label { color: #e53e3e; }
        .module-node.warning .tree-label { color: #ed8936; }
        .module-node.ok .tree-label { color: #38a169; }
        
        .fiber-node.error .tree-label { color: #e53e3e; }
        .fiber-node.warning .tree-label { color: #ed8936; }
        .fiber-node.ok .tree-label { color: #38a169; }
        
        .fiber-details {
            margin: 8px 0;
            padding: 16px;
            background: #f7fafc;
            border-left: 3px solid #e2e8f0;
        }
    `;
  }

  /**
   * Styles des filtres - Minimaliste
   */
  static getFilterStyles() {
    return `
        .filters-section {
            background: #f7fafc;
            border-left: 4px solid #4299e1;
            margin-bottom: 30px;
            overflow: hidden;
        }
        .filters-header {
            background: #e2e8f0;
            padding: 12px 20px;
            font-weight: 600;
            font-size: 13px;
            color: #2d3748;
            text-transform: uppercase;
            letter-spacing: 1px;
        }
        .filters-content {
            padding: 20px;
            display: grid;
            grid-template-columns: 200px 1fr;
            gap: 24px;
        }
        
        .filter-group {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }
        .filter-title {
            font-weight: 600;
            color: #2d3748;
            font-size: 14px;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        .filter-options {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }
        .rules-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 8px;
        }
        
        .filter-option {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            padding: 6px 0;
            transition: color 0.2s ease;
        }
        .filter-option:hover {
            color: #4299e1;
        }
        .filter-option input[type="radio"],
        .filter-option input[type="checkbox"] {
            margin: 0;
            cursor: pointer;
            accent-color: #4299e1;
        }
        .filter-label {
            font-size: 13px;
            color: #4a5568;
            cursor: pointer;
            user-select: none;
            font-weight: 500;
        }
    `;
  }

  /**
   * Styles des chips d'état - Minimaliste
   */
  static getStateChipStyles() {
    return `
        .state-chip {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border: 1px solid;
        }
        .state-chip.libre {
            background: #f0fff4;
            color: #38a169;
            border-color: #68d391;
        }
        .state-chip.occupee {
            background: #fffaf0;
            color: #ed8936;
            border-color: #fbb042;
        }
        
        .bpo-chip {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
            background: #ebf8ff;
            color: #3182ce;
            border: 1px solid #90cdf4;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
    `;
  }

  /**
   * Styles des messages - Minimaliste
   */
  static getMessageStyles() {
    return `
        .tree-message {
            margin: 12px 0;
            padding: 16px;
            border-left: 4px solid #e2e8f0;
            background: #f7fafc;
        }
        .tree-message.error {
            background: #fed7d7;
            border-left-color: #e53e3e;
        }
        .tree-message.warning {
            background: #feebc8;
            border-left-color: #ed8936;
        }
        .message-title {
            font-weight: 600;
            margin-bottom: 6px;
            color: #2d3748;
            font-size: 14px;
        }
        .message-content {
            font-size: 13px;
            color: #4a5568;
            line-height: 1.5;
        }
        
        .ok-message {
            color: #38a169;
            font-weight: 600;
            text-align: center;
            padding: 12px;
            font-style: italic;
            background: #f0fff4;
            border: 1px solid #68d391;
            border-radius: 4px;
        }
        .sor-filename {
            font-family: inherit;
            background: #f7fafc;
            color: #4a5568;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: inherit;
            font-weight: 500;
            border: none;
            white-space: nowrap;
            display: inline;
        }
        .pbo-value {
            font-family: inherit;
            background: #f0fdf4;
            color: #166534;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: inherit;
            font-weight: 500;
            border: none;
            white-space: nowrap;
            display: inline;
        }
    `;
  }

  /**
   * Styles spécifiques Rule08 - Tableaux événements
   */
  static getRule08Styles() {
    return `
    .rule08-table { 
      border-collapse: collapse; 
      width: 100%; 
      font-family: monospace; 
      margin: 16px 0;
    }
    .rule08-thead { 
      background-color: #f8f9fa; 
    }
    .rule08-header-row { 
      border-bottom: 2px solid #dee2e6; 
    }
    .rule08-th { 
      padding: 8px; 
      border: 1px solid #dee2e6; 
    }
    .rule08-th-left { 
      text-align: left; 
    }
    .rule08-th-center { 
      text-align: center; 
    }
    .rule08-outlier-row { 
      background-color: #fff5f5; 
      border-bottom: 1px solid #dee2e6; 
    }
    .rule08-consensus-row { 
      background-color: #f8fff4; 
      border-bottom: 1px solid #dee2e6; 
    }
    .rule08-td { 
      padding: 8px; 
      border: 1px solid #dee2e6; 
    }
    .rule08-td-center { 
      text-align: center; 
    }
    .rule08-error-status { 
      color: #dc3545; 
    }
    .rule08-success-status { 
      color: #28a745; 
    }
  `;
  }

  /**
   * Styles du pied de page - Minimaliste
   */
  static getFooterStyles() {
    return `
        .footer { 
            margin-top: 50px; 
            text-align: center; 
            color: #718096; 
            font-size: 0.9em;
            padding: 20px 0;
            border-top: 1px solid #e2e8f0;
        }
        
        .footer p {
            margin: 0 0 12px 0;
        }
        
        .footer-contact {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            flex-wrap: wrap;
        }
        
        .footer-contact a {
            color: #4299e1;
            text-decoration: none;
            font-weight: 500;
            transition: color 0.2s ease;
        }
        
        .footer-contact a:hover {
            color: #2b6cb0;
            text-decoration: underline;
        }
        
        .footer-contact .separator {
            color: #cbd5e0;
            font-weight: 300;
        }
        
        @media (max-width: 480px) {
            .footer-contact {
                flex-direction: column;
                gap: 8px;
            }
            .footer-contact .separator {
                display: none;
            }
        }
        
        .no-issues {
            text-align: center;
            padding: 40px;
            color: #718096;
            font-style: italic;
            background: #f7fafc;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
        }
    `;
  }

  /**
   * Styles responsive - Minimaliste
   */
  static getResponsiveStyles() {
    return `
        @media (max-width: 768px) {
            body { padding: 20px; }
            .header h1 { font-size: 2.2em; }
            .summary-grid { grid-template-columns: 1fr 1fr; }
            .summary-item { padding: 20px; }
            .tree-stats { display: none; }
            .tree-meta { font-size: 11px; }
            .filters-content {
                grid-template-columns: 1fr;
                gap: 20px;
            }
            .rules-grid {
                grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
            }
            .tree-children {
                margin-left: 16px;
            }
        }
    `;
  }
}
